import { useMemo, useRef, useEffect, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import config from '../devprodConfig';
import 'leaflet-draw';
import { useReportTemplateContext } from './report/ReportTemplate/ReportTemplateContext';
import { useRakennusData } from '../context/RakennusDataContext';
import { useParams } from 'react-router-dom';
import HuomioBox from './utils/HuomioBox';

const apiurl = config.apiBaseUrl;
const maptemplate = config.mapTileTemplate;

const MapVisualMultiple = ({ data, onChange, height = '420px', onRequestFullscreen, showExtraControls = true, onSelectBuilding, onMarkerClick, highlightedBuildingKey }) => {
  const { reportId } = useParams();
  const { rakennusData: kiinteisto } = useRakennusData();

  let mergedRakennusData;
  try {
    const context = useReportTemplateContext();
    if (!context) throw new Error('useReportTemplateContext must be used within a ReportTemplateProvider');
    mergedRakennusData = context.mergedRakennusData;
  } catch {
    mergedRakennusData = null;
  }

  const markerOverrides = data?.markerOverrides ?? {};

  const mapHeight = height;
  const rakennukset_fulls = useMemo(() => {
    if (mergedRakennusData?.rakennukset_fulls?.length) return mergedRakennusData.rakennukset_fulls;
    if (kiinteisto?.rakennukset_fulls?.length) return kiinteisto.rakennukset_fulls;
    return [];
  }, [mergedRakennusData, kiinteisto]);

  const mapRef = useRef(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const dataRef = useRef(data);
  dataRef.current = data;
  const markerOverridesRef = useRef(markerOverrides);
  markerOverridesRef.current = markerOverrides;
  // Ref, not a dependency of the map-rebuild effect below — the parent
  // typically passes a fresh inline function each render, and this way
  // a popup button always calls the latest one without the whole
  // Leaflet map (and its markers/layers) rebuilding on every render.
  const onSelectBuildingRef = useRef(onSelectBuilding);
  onSelectBuildingRef.current = onSelectBuilding;
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;

  // Leaflet marker instances keyed by marker index — for imperative position updates
  const leafletMarkersRef = useRef({});
  // Refs to base layer instances so the explicit layer selector can switch them imperatively
  const leafletLayersRef = useRef({});
  const leafletMapRef = useRef(null);

  // Base marker data from building data only — no overrides here so this doesn't
  // change when the user drags a marker and overrides are saved
  const baseMarkers = useMemo(() => {
    return rakennukset_fulls
      .map((r, idx) => {
        const coords = r?.sijainti?.coordinates;
        if (!coords || coords.length < 2) return null;
        return {
          lat: coords[1],
          lng: coords[0],
          label: `${r?.rakennustunnus} ${r?.rakennusluokitus}` || `Rakennus ${idx + 1}`,
          idx,
          isMain: !!r.isMainBuilding,
          rakennus: r,
        };
      })
      .filter(Boolean);
  }, [rakennukset_fulls]);

  // Create / destroy the map only when building data changes (not on every drag)
  useEffect(() => {
    if (!baseMarkers.length || !mapRef.current) return;

    leafletMarkersRef.current = {};

    const mapContainer = mapRef.current;
    mapContainer.style.height = mapHeight;
    mapContainer.style.width = '100%';

    // Apply any existing overrides at creation time via the ref (not reactive state)
    const initialMarkers = baseMarkers.map(m => {
      const ov = markerOverridesRef.current[m.idx];
      return ov ? { ...m, lat: ov.lat, lng: ov.lng } : m;
    });

    const map = L.map(mapContainer, {
      center: [initialMarkers[0].lat, initialMarkers[0].lng],
      zoom: 17,
      minZoom: 12,
      maxZoom: 17,
      maxNativeZoom: 17,
      dragging: true,
      scrollWheelZoom: true,
      zoomControl: false,
      detectRetina: true,
    });
    mapRef.current._leaflet_map = map;

    // --- Tile layers ---
    const template = apiurl + maptemplate;
    const defaultTileMatrixSet = 'WGS84_Pseudo-Mercator';
    const getTileUrl = (layerName, tileMatrixSet) =>
      template.replace('{layerName}', layerName).replace('{tileMatrixSet}', tileMatrixSet);

    const layerBasePublic = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors | ' +
        '<a href="https://ktp.waativa.fi/">Waativa Oy Kiinteistötietopankki</a>',
      maxNativeZoom: 17,
      crossOrigin: true,
    });
    const layerBaseMapIlmakuva = L.tileLayer(getTileUrl('ortokuva', defaultTileMatrixSet), { attribution: '&copy; Maanmittauslaitos', detectRetina: true });
    const layerBaseMapSelko = L.tileLayer(getTileUrl('selkokartta', defaultTileMatrixSet), { attribution: '&copy; Maanmittauslaitos', detectRetina: true });
    const layerKiinteistorajat = L.tileLayer(getTileUrl('kiinteistojaotus', defaultTileMatrixSet), { detectRetina: true });
    const layerKiinteistotunnukset = L.tileLayer(getTileUrl('kiinteistotunnukset', defaultTileMatrixSet), { detectRetina: true });
    const layerBaseMap = L.tileLayer(getTileUrl('taustakartta', defaultTileMatrixSet), {
      attribution: '&copy; Maanmittauslaitos',
      detectRetina: true,
    });
    const layerMaastokartta = L.tileLayer(getTileUrl('maastokartta', defaultTileMatrixSet), { attribution: '&copy; Maanmittauslaitos', detectRetina: true });

    // --- SVG marker setup ---
    // The hover-scale class goes on an INNER wrapper, not the divIcon's
    // own outer element — Leaflet positions markers via its own inline
    // `transform: translate3d(...)` on that outer element (updated on
    // every pan/zoom), which silently wins over any CSS `transform` rule
    // targeting the same element since inline styles always beat
    // stylesheet rules on the same property. A separate inner div has no
    // transform of Leaflet's own, so the hover scale actually applies.
    const markerSVG = `
      <div class="map-marker-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="50" viewBox="0 0 24 24" style="pointer-events:none;">
          <path fill="#007acc" stroke="white" stroke-width="0.75"
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
        </svg>
      </div>
    `;
    const customSvgIcon = L.divIcon({
      html: markerSVG,
      className: '',
      iconSize: [40, 50],
      iconAnchor: [20, 50],
      popupAnchor: [0, -50],
    });

    // Non-main buildings (e.g. outbuildings, garages) on the same lot —
    // smaller and a light orange, distinct from the main building's blue
    // marker so it still reads as the primary one at a glance.
    const secondarySvg = `
      <div class="map-marker-icon">
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="35" viewBox="0 0 24 24" style="pointer-events:none;">
          <path fill="#f4a261" stroke="white" stroke-width="0.75"
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
        </svg>
      </div>
    `;
    const secondarySvgIcon = L.divIcon({
      html: secondarySvg,
      className: '',
      iconSize: [28, 35],
      iconAnchor: [14, 35],
      popupAnchor: [0, -35],
    });

    const markerLayer = L.layerGroup();
    initialMarkers.forEach(({ lat, lng, label, idx, isMain, rakennus }) => {
      const marker = L.marker([lat, lng], { icon: isMain ? customSvgIcon : secondarySvgIcon, draggable: true });
      if (onMarkerClickRef.current) {
        // Karttanäkymä modal: clicking a marker highlights it in the
        // building list beside the map instead of popping up a card —
        // the list is right there, so a popup would just be a second,
        // redundant way to get to the same "open tiedot" action.
        marker.on('click', () => onMarkerClickRef.current?.(rakennus));
      } else if (onSelectBuildingRef.current) {
        // Popup offers opening the full Rakennuksen tiedot modal for this
        // building instead of jumping straight there on a bare click —
        // a real DOM node (not an HTML string) so the button's handler
        // is a normal closure, no manual popupopen/event-delegation needed.
        const popupEl = document.createElement('div');
        popupEl.style.padding = '2px 0 0';

        const tunnusEl = document.createElement('div');
        tunnusEl.textContent = rakennus?.rakennustunnus || label;
        tunnusEl.style.cssText = 'font-size:0.85rem;font-weight:600;color:#1c1c1e;';
        popupEl.appendChild(tunnusEl);

        if (rakennus?.rakennusluokitus) {
          const luokitusEl = document.createElement('div');
          luokitusEl.textContent = rakennus.rakennusluokitus;
          luokitusEl.style.cssText = 'font-size:0.74rem;color:#8e8e93;margin-bottom:10px;';
          popupEl.appendChild(luokitusEl);
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = 'Avaa tiedot';
        btn.className = 'btn btn-sm btn-success w-100';
        btn.style.cssText = 'font-weight:600;border-radius:7px;margin-top:4px;';
        btn.onclick = () => onSelectBuildingRef.current?.(rakennus);
        popupEl.appendChild(btn);

        marker.bindPopup(popupEl, { minWidth: 170, className: 'building-popup' });
      } else if (label) {
        marker.bindPopup(label);
      }
      marker.on('dragend', () => {
        const { lat: newLat, lng: newLng } = marker.getLatLng();
        const current = dataRef.current ?? {};
        const overrides = { ...(current.markerOverrides ?? {}), [idx]: { lat: newLat, lng: newLng } };
        onChangeRef.current?.({ ...current, markerOverrides: overrides });
      });
      marker.addTo(markerLayer);
      leafletMarkersRef.current[idx] = marker;
    });
    markerLayer.addTo(map);

    // --- Auto-fit map to markers ---
    const bounds = L.latLngBounds(initialMarkers.map(m => [m.lat, m.lng]));
    map.fitBounds(bounds, { padding: [50, 50] });

    // --- Draw controls ---
    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    // Restore previously saved drawn items
    const savedGeoJSON = dataRef.current?.drawnItemsGeoJSON;
    if (savedGeoJSON?.features?.length) {
      L.geoJSON(savedGeoJSON, {
        pointToLayer: (_, latlng) => L.marker(latlng, { icon: customSvgIcon }),
      }).eachLayer(layer => drawnItems.addLayer(layer));
    }

    const saveDrawnItems = () => {
      onChangeRef.current?.({ ...dataRef.current, drawnItemsGeoJSON: drawnItems.toGeoJSON() });
    };

    const drawControl = new L.Control.Draw({
      position: 'topright',
      draw: { polyline: true, polygon: true, rectangle: true, circle: true, marker: { icon: customSvgIcon } },
      edit: { featureGroup: drawnItems, remove: true },
    });
    // Drawing/measuring tools only make sense with room to work in — the
    // small embedded landing-page map just clutters up with them; the
    // fullscreen view (showExtraControls stays true there) keeps them.
    if (showExtraControls) map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      drawnItems.addLayer(layer);
      if (layer instanceof L.Polyline) {
        const latlngs = layer.getLatLngs();
        let distance = 0;
        for (let i = 1; i < latlngs.length; i++) distance += latlngs[i - 1].distanceTo(latlngs[i]);
        layer.bindTooltip(`Matka: ${distance.toFixed(1)} m`, { permanent: true, className: 'measurement-tooltip' });
      }
      if (layer instanceof L.Polygon) {
        const latlngs = layer.getLatLngs()[0];
        const area = L.GeometryUtil.geodesicArea(latlngs);
        layer.bindTooltip(`Pinta-ala: ${area.toFixed(1)} m²`, { permanent: true, className: 'measurement-tooltip' });
      }
      if (layer instanceof L.Circle) {
        const radius = layer.getRadius();
        layer.bindTooltip(`Säde: ${radius.toFixed(1)} m`, { permanent: true, className: 'measurement-tooltip' });
      }
      saveDrawnItems();
    });
    map.on(L.Draw.Event.EDITED, saveDrawnItems);
    map.on(L.Draw.Event.DELETED, saveDrawnItems);

    const baseMaps = { 'OpenStreetMap': layerBasePublic, 'Ilmakuva': layerBaseMapIlmakuva, 'Selkokartta': layerBaseMapSelko, 'Taustakartta': layerBaseMap, 'Maastokartta': layerMaastokartta };
    const overlayMaps = { 'Kiinteistörajat': layerKiinteistorajat, 'Kiinteistötunnukset': layerKiinteistotunnukset, 'Rakennusmerkit': markerLayer, 'Drawn Items': drawnItems };

    const layerKeyMap = {
      'OpenStreetMap': 'osm',
      'Ilmakuva': 'ortokuva',
      'Selkokartta': 'selkokartta',
      'Taustakartta': 'taustakartta',
      'Maastokartta': 'maastokartta',
    };
    const layerByKey = {
      osm: layerBasePublic,
      ortokuva: layerBaseMapIlmakuva,
      selkokartta: layerBaseMapSelko,
      taustakartta: layerBaseMap,
      maastokartta: layerMaastokartta,
    };
    leafletLayersRef.current = layerByKey;
    leafletMapRef.current = map;

    const savedLayer = dataRef.current?.mapLayer ?? 'taustakartta';
    (layerByKey[savedLayer] ?? layerBasePublic).addTo(map);
    layerKiinteistorajat.addTo(map);

    map.on('baselayerchange', (e) => {
      const key = layerKeyMap[e.name] ?? 'osm';
      onChangeRef.current?.({ ...dataRef.current, mapLayer: key });
    });

    if (showExtraControls) L.control.layers(baseMaps, overlayMaps, { collapsed: true }).addTo(map);
    L.control.zoom({ position: 'topright' }).addTo(map);

    setTimeout(() => map.invalidateSize(), 0);

    return () => { map.remove(); leafletMarkersRef.current = {}; leafletLayersRef.current = {}; leafletMapRef.current = null; };
  }, [baseMarkers, mapHeight, showExtraControls]);

  // Imperatively update marker positions when overrides change —
  // avoids destroying/recreating the whole map on every drag
  useEffect(() => {
    const lMarkers = leafletMarkersRef.current;
    baseMarkers.forEach(({ lat, lng, idx }) => {
      const lm = lMarkers[idx];
      if (!lm) return;
      const ov = markerOverrides[idx];
      lm.setLatLng(ov ? [ov.lat, ov.lng] : [lat, lng]);
    });
  }, [markerOverrides, baseMarkers]);

  // Visually highlight the marker matching highlightedBuildingKey (set
  // from the TOC sidebar) — a separate, lightweight effect rather than a
  // dependency of the map-rebuild effect above, so selecting a building
  // doesn't tear down and rebuild the whole Leaflet map every time.
  useEffect(() => {
    // Scoped to this instance's own map container, not document-wide —
    // more than one MapVisualMultiple can be mounted at once (embedded
    // view + fullscreen modal), and a global query would clear the wrong
    // one's highlight.
    const prev = mapRef.current?.querySelector('.map-marker-icon.marker-highlighted');
    prev?.classList.remove('marker-highlighted');
    if (highlightedBuildingKey == null) return;
    const match = baseMarkers.find(m => (m.rakennus?.id_rakennus || m.rakennus?.rakennustunnus) === highlightedBuildingKey);
    if (!match) return;
    const marker = leafletMarkersRef.current[match.idx];
    marker?.getElement()?.querySelector('.map-marker-icon')?.classList.add('marker-highlighted');
  }, [highlightedBuildingKey, baseMarkers]);

  const hasOverrides = Object.keys(markerOverrides).length > 0;

  const MAP_LAYERS = [
    { key: 'osm', label: 'OpenStreetMap' },
    { key: 'taustakartta', label: 'Taustakartta' },
    { key: 'ortokuva', label: 'Ilmakuva' },
    { key: 'selkokartta', label: 'Selkokartta' },
    { key: 'maastokartta', label: 'Maastokartta' },
  ];

  const [activeLayer, setActiveLayer] = useState(data?.mapLayer ?? 'taustakartta');

  const switchLayer = (key) => {
    const map = leafletMapRef.current;
    const layers = leafletLayersRef.current;
    if (map && layers) {
      Object.values(layers).forEach(l => { if (map.hasLayer(l)) map.removeLayer(l); });
      (layers[key] ?? layers.taustakartta).addTo(map);
    }
    setActiveLayer(key);
    onChange?.({ ...(data ?? {}), mapLayer: key });
  };

  return <>
    {/* Leaflet's divIcon wrapper is the actual hit-target — scoping the
        hover transform to it (not the inner SVG, which has pointer-
        events:none) and anchoring the transform at the pin's own tip
        (50% 100%, matching both icons' iconAnchor ratio) so it grows in
        place instead of visibly drifting off its coordinate on hover. */}
    <style>{`
      .map-marker-icon { transition: transform 0.15s ease, filter 0.15s ease; transform-origin: 50% 100%; cursor: pointer; }
      .map-marker-icon:hover { transform: scale(1.18); }
      .map-marker-icon.marker-highlighted { filter: drop-shadow(0 0 5px #198754) drop-shadow(0 0 2px #198754); }
    `}</style>
    {hasOverrides && (
      <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: '0.82rem' }}>
        <span className="text-success"><i className="bi bi-pin-map-fill" /> Markkerit siirretty</span>
        <button
          className="btn btn-outline-secondary btn-sm"
          style={{ fontSize: '0.78rem', padding: '1px 8px' }}
          onClick={() => onChange?.({ ...(data ?? {}), markerOverrides: {} })}
        >Palauta alkuperäiset</button>
      </div>
    )}
    <div style={{ position: 'relative' }}>
      <div ref={mapRef} style={{ height: mapHeight, maxWidth: '100%', borderRadius: '12px' }} />
      {onRequestFullscreen && (
        <button
          type="button"
          onClick={onRequestFullscreen}
          title="Suurenna kartta"
          aria-label="Suurenna kartta"
          className="btn btn-sm btn-light shadow-sm d-flex align-items-center justify-content-center p-0"
          style={{ position: 'absolute', top: 10, left: 10, width: 30, height: 30, borderRadius: 6, zIndex: 1000, border: '1px solid rgba(0,0,0,0.2)' }}
        >
          <i className="bi bi-arrows-fullscreen" />
        </button>
      )}
    </div>
    {showExtraControls && (
      <div className="mt-2" style={{ padding: '0 10px' }}>
        {/* Hover background via a CSS class — inline `background:'none'`
            on the inactive buttons would otherwise win over a :hover
            rule on the same element (same trap as the marker/sidebar
            hover effects above). */}
        <style>{`
          .map-layer-btn { transition: background-color 0.12s ease; }
          .map-layer-btn:not(.active):hover { background: #dfe1e4; }
        `}</style>
        <div role="group" aria-label="Karttataso" style={{ display: 'flex', flexWrap: 'wrap', gap: 2, background: '#eceef0', borderRadius: 9, padding: 3 }}>
          {MAP_LAYERS.map(({ key, label }) => {
            const active = activeLayer === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => switchLayer(key)}
                className={`map-layer-btn${active ? ' active' : ''}`}
                style={{
                  flex: '1 1 auto', minWidth: 90, border: 'none', borderRadius: 7, padding: '6px 10px',
                  fontSize: '0.74rem', fontWeight: active ? 700 : 600,
                  background: active ? '#198754' : undefined, color: active ? '#fff' : '#6c6c70', cursor: 'pointer',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    )}
  </>;
};

export default MapVisualMultiple;
